// ============================================================
// TMR Portal Admin — gestão de Usuários (equipe interna da TMR)
//
// Wired to the Identity backend (10 B5). Creating here invites the person into Clerk (06 §6.1) — this is
// the single front door for starting someone's access, which is what the screen always implied and, until
// 2026-07-28, never did.
//
// The invite column is the reason the contingency queue is visible at all: an approved account whose
// invitation is still queued looks identical to a delivered one from the outside, and the person waiting
// for that e-mail has no way to tell anybody.
// ============================================================
import { useState } from 'react';
import { Avatar, Btn, StatusBadge } from '../../components/primitives';
import { PageHeader, SearchBox, FilterTabs, Table, Td } from '../../components/ui';
import { Skeleton, EmptyState, ErrorState } from '../../components/states';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { CopyableText } from '../../components/CopyableText';
import { Pagination } from '../../components/Pagination';
import { Icons, StrokeIcon } from '../../lib/icons';
import { PERMISSIONS, useCapabilities, usePermission } from '../../data/PermissionsProvider';
import { useDebounced } from '../../useDebounced';
import { USER_PAGE_SIZE, useCreateUser, useReissueUserInvite, useSetUserDisabled, useUpdateUser, useUserList, userExportPage } from '../../data/users/queries';
import { ExportMenu } from '../../components/ExportMenu';
import { USER_EXPORT_COLUMNS } from './exportColumns';
import type { LegacyLinkOutcome, UserListItem } from '../../data/users/contract';
import { legacyLinkReason } from '../../data/users/legacyLink';
import { canOfferLegacyAccess } from '../../data/users/legacyAccess';
import { UserFormModal } from './UserFormModal';
import type { UserFormValues } from './UserFormModal';
import { UserRolesModal } from './UserRolesModal';
import { LinkLegacyUserModal } from './LinkLegacyUserModal';
import { CreateLegacyAccessModal } from './CreateLegacyAccessModal';

type StatusFilter = 'todos' | 'ativos' | 'inativos';

/** What the operator is told after a create, so a queued invitation is never mistaken for a sent one. */
interface CreateOutcome {
  /** The account just created, so the banner can offer to open the legacy picker on it. */
  user: UserListItem;
  inviteSent: boolean;
  /** How the automatic correlation to the legacy went (05 I7). */
  legacyLink: LegacyLinkOutcome;
}

/** What "Reenviar convite" did, kept with the name so the line can say whose invitation it was. */
type ReissueOutcome = { name: string; sent: boolean } | { name: string; error: string };

export function UsersPage() {
  const canManage = usePermission(PERMISSIONS.usersManage);
  const canGrantLegacyAccess = usePermission(PERMISSIONS.usersLegacyAccessGrant);
  // The routes behind the action exist only where the backend reaches the legacy (33 §7).
  const legacyAccessAvailable = useCapabilities().legacyBackofficeAccess === true;
  const offersLegacyAccess = (user: UserListItem) =>
    canOfferLegacyAccess(user, canGrantLegacyAccess, legacyAccessAvailable);

  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<StatusFilter>('todos');
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<UserListItem | null>(null);
  const [toDisable, setToDisable] = useState<UserListItem | null>(null);
  const [managingRoles, setManagingRoles] = useState<UserListItem | null>(null);
  const [linkingLegacy, setLinkingLegacy] = useState<UserListItem | null>(null);
  const [creatingLegacyAccess, setCreatingLegacyAccess] = useState<UserListItem | null>(null);
  const [outcome, setOutcome] = useState<CreateOutcome | null>(null);
  const [toReissue, setToReissue] = useState<UserListItem | null>(null);
  const [reissueOutcome, setReissueOutcome] = useState<ReissueOutcome | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const debouncedSearch = useDebounced(search);

  // Described once: the table pages through these filters and the export walks all of them.
  const filters = {
    search: debouncedSearch || undefined,
    disabled: tab === 'todos' ? undefined : tab === 'inativos',
  };

  const list = useUserList({ ...filters, page });

  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const setDisabled = useSetUserDisabled();
  const reissueInvite = useReissueUserInvite();

  // The server gates this list; without the permission there is nothing to show and every request would
  // 403 (skill `frontend-architecture`, Regra 5).
  if (!canManage) {
    return (
      <div className="pa-screen" style={{ padding: 24 }}>
        <PageHeader eyebrow="Sistema" title="Usuários" desc="A equipe interna da TMR." />
        <EmptyState
          message="Você não tem permissão para gerir usuários."
          hint="Peça o acesso a quem administra os papéis."
        />
      </div>
    );
  }

  const resetTo = (change: () => void) => {
    change();
    setPage(1);
  };

  const submitNew = (values: UserFormValues) => {
    setFormError(null);
    createUser.mutate(
      {
        email: values.email,
        name: values.name,
        phone: values.phone || null,
        grants: values.grants,
      },
      {
        onSuccess: (result) => {
          setShowNew(false);
          // Enough of the new account for the banner to open the legacy picker on it without waiting for
          // the list to come back — the fields that modal reads are exactly these.
          setOutcome({
            user: {
              id: result.id,
              email: result.email,
              name: values.name,
              phone: values.phone || null,
              disabled: false,
              localFallbackEnabled: false,
              legacyUserId: result.legacyUserId,
              hasClerkAccount: false,
              createdAt: new Date().toISOString(),
              activeRoleCount: values.grants?.length ?? 0,
            },
            inviteSent: result.inviteSent,
            legacyLink: result.legacyLink,
          });
        },
        onError: (error) => setFormError(messageOf(error)),
      },
    );
  };

  const submitEdit = (values: UserFormValues) => {
    if (!editing) return;
    setFormError(null);
    updateUser.mutate(
      { id: editing.id, name: values.name, phone: values.phone || null },
      {
        onSuccess: () => setEditing(null),
        onError: (error) => setFormError(messageOf(error)),
      },
    );
  };

  const items = list.data?.items ?? [];
  const total = list.data?.totalCount ?? 0;

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Sistema"
        title="Usuários"
        desc="A equipe interna da TMR. Cadastrar envia um convite por e-mail."
        actions={
          <>
            <ExportMenu
              spec={{
                fileName: 'usuarios',
                columns: USER_EXPORT_COLUMNS,
                fetchPage: userExportPage(filters),
              }}
              totalCount={total}
              disabled={list.isPending}
            />
            <Btn variant="primary" icon={Icons.plus} onClick={() => { setFormError(null); setShowNew(true); }}>
              Novo usuário
            </Btn>
          </>
        }
      />

      {outcome && (
        <div
          role="status"
          style={{
            padding: '10px 14px',
            border: `1px solid ${outcome.inviteSent ? 'var(--border-default)' : 'var(--border-strong)'}`,
            borderRadius: 'var(--radius-sm)',
            background: 'var(--cream-100)',
            fontSize: 12.5,
            lineHeight: 1.55,
            color: 'var(--fg-muted)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
          }}
        >
          <StrokeIcon d={outcome.inviteSent ? Icons.check : Icons.alert} size={15} style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ flex: 1 }}>
            {outcome.inviteSent ? (
              <>
                <strong>{outcome.user.name}</strong> foi cadastrada e o convite foi enviado.
              </>
            ) : (
              <>
                <strong>{outcome.user.name}</strong> foi cadastrada, mas o convite <strong>ainda não saiu</strong>.
                O envio será repetido automaticamente — ninguém recebeu e-mail ainda.{' '}
                <button
                  type="button"
                  onClick={() => setToReissue(outcome.user)}
                  style={{ padding: 0, border: 0, background: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, color: 'var(--forest-800)', textDecoration: 'underline' }}
                >
                  Reenviar agora
                </button>
              </>
            )}
            {/* O vínculo com o legado é invisível quando falta: sem ele, o campo Gestor da Conta vai vazio
                para o legado, sem erro nenhum. Por isso o desfecho é dito aqui, com a saída manual ao lado. */}
            {outcome.legacyLink === 'Linked' ? (
              <>
                {' '}
                Também foi vinculada ao legado{' '}
                <span style={{ fontFamily: 'var(--font-mono)' }}>#{outcome.user.legacyUserId}</span>.
              </>
            ) : (
              <>
                {' '}
                {legacyLinkReason(outcome.legacyLink)}{' '}
                <button
                  type="button"
                  onClick={() => setLinkingLegacy(outcome.user)}
                  style={{ padding: 0, border: 0, background: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, color: 'var(--forest-800)', textDecoration: 'underline' }}
                >
                  Vincular ao legado
                </button>
                {/* A new hire has no legacy login to link: creating one is the next step (33 §3). */}
                {outcome.legacyLink === 'NotFound' && offersLegacyAccess(outcome.user) && (
                  <>
                    {' ou '}
                    <button
                      type="button"
                      onClick={() => setCreatingLegacyAccess(outcome.user)}
                      style={{ padding: 0, border: 0, background: 'none', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, color: 'var(--forest-800)', textDecoration: 'underline' }}
                    >
                      Criar acesso no Portal Interno
                    </button>
                  </>
                )}
              </>
            )}
          </span>
          <button type="button" onClick={() => setOutcome(null)} className="pa-icon-btn" style={{ width: 26, height: 26 }} title="Dispensar">
            <StrokeIcon d={Icons.x} size={13} />
          </button>
        </div>
      )}

      {reissueOutcome && (
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: 'var(--fg-muted)' }}>
          <StrokeIcon d={'sent' in reissueOutcome && reissueOutcome.sent ? Icons.check : Icons.alert} size={14} />
          <span style={{ flex: 1 }}>
            {'error' in reissueOutcome ? (
              <>Não foi possível reenviar o convite de <strong>{reissueOutcome.name}</strong>: {reissueOutcome.error}</>
            ) : reissueOutcome.sent ? (
              <>Convite reenviado para <strong>{reissueOutcome.name}</strong>. O link anterior não vale mais.</>
            ) : (
              <>
                O convite anterior de <strong>{reissueOutcome.name}</strong> foi cancelado, mas o novo{' '}
                <strong>ainda não saiu</strong>. O envio será repetido automaticamente.
              </>
            )}
          </span>
          <button type="button" onClick={() => setReissueOutcome(null)} className="pa-icon-btn" style={{ width: 26, height: 26 }} title="Dispensar">
            <StrokeIcon d={Icons.x} size={13} />
          </button>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
        <FilterTabs
          tabs={[
            { value: 'todos', label: 'Todos' },
            { value: 'ativos', label: 'Ativos' },
            { value: 'inativos', label: 'Inativos' },
          ]}
          value={tab}
          onChange={(v) => resetTo(() => setTab(v as StatusFilter))}
        />
        <SearchBox
          value={search}
          onChange={(v) => resetTo(() => setSearch(v))}
          placeholder="Buscar por nome ou e-mail…"
          width={320}
        />
      </div>

      {list.isPending ? (
        <Skeleton rows={6} />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          message={debouncedSearch ? 'Nenhum usuário encontrado para esta busca.' : 'Nenhum usuário cadastrado ainda.'}
          hint={debouncedSearch ? 'Ajuste a busca ou o filtro.' : undefined}
        />
      ) : (
        <>
          <Table
            columns={[
              { label: 'Usuário' },
              { label: 'E-mail' },
              { label: 'Acesso' },
              { label: 'Papéis' },
              { label: 'Status' },
              { label: '', align: 'right', width: 150 },
            ]}
          >
            {items.map((user) => (
              <tr key={user.id} className="pa-row">
                <Td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 11, opacity: user.disabled ? 0.55 : 1 }}>
                    <Avatar name={user.name} size={34} />
                    <span>
                      <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--ink-900)' }}>{user.name}</div>
                      {user.phone && (
                        <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>{user.phone}</div>
                      )}
                    </span>
                  </div>
                </Td>
                <Td>
                  <CopyableText value={user.email} style={{ fontSize: 13, color: 'var(--fg-muted)' }} />
                  {/* O vínculo com o legado vira ação porque a ausência dele é consequente e silenciosa:
                      um Gestor da Conta sem vínculo replica `gestor_conta` vazio (05 I7). */}
                  <button
                    type="button"
                    onClick={() => setLinkingLegacy(user)}
                    title={user.legacyUserId !== null ? 'Ver ou desfazer o vínculo com o legado' : 'Vincular ao cadastro do legado'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 2, padding: 0, border: 0, background: 'none', cursor: 'pointer', fontSize: 11.5, color: user.legacyUserId !== null ? 'var(--fg-subtle)' : 'var(--forest-800)' }}
                  >
                    <StrokeIcon d={user.legacyUserId !== null ? Icons.swap : Icons.plus} size={11} />
                    {user.legacyUserId !== null ? (
                      <span style={{ fontFamily: 'var(--font-mono)' }}>legado #{user.legacyUserId}</span>
                    ) : (
                      'Vincular ao legado'
                    )}
                  </button>
                  {offersLegacyAccess(user) && (
                    <button
                      type="button"
                      onClick={() => setCreatingLegacyAccess(user)}
                      title="Criar o login desta pessoa no Portal Interno"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 2, marginLeft: 12, padding: 0, border: 0, background: 'none', cursor: 'pointer', fontSize: 11.5, color: 'var(--forest-800)' }}
                    >
                      <StrokeIcon d={Icons.shield} size={11} />
                      Criar no Portal Interno
                    </button>
                  )}
                </Td>
                <Td>
                  {/* O convite sai daqui, então é aqui que precisa dar para ver se ele funcionou. Sem
                      isto, cadastrar alguém e ninguém aparecer do outro lado são a mesma linha. Diz só
                      o que esta lista sabe — se existe conta no Clerk. Por que o convite não chegou é
                      assunto da tela de Acessos, que tem a entrega; mandar outro dá para fazer daqui
                      também, pela ação "Reenviar convite" da linha. */}
                  <StatusBadge status={user.hasClerkAccount ? 'acesso_criado' : 'acesso_aguardando'} />
                </Td>
                <Td>
                  {user.activeRoleCount === 0 ? (
                    // The invisible failure this column exists for: the account works, and does nothing.
                    <button
                      type="button"
                      onClick={() => setManagingRoles(user)}
                      title="Conceder um papel"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', border: '1px solid var(--border-strong)', borderRadius: 999, background: 'var(--cream-100)', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--ink-900)' }}
                    >
                      <StrokeIcon d={Icons.alert} size={12} />
                      Sem papel
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setManagingRoles(user)}
                      title="Ver e gerir papéis"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', border: '1px solid var(--border-default)', borderRadius: 999, background: 'var(--cream-50)', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12, color: 'var(--forest-800)' }}
                    >
                      <StrokeIcon d={Icons.shield} size={12} />
                      {user.activeRoleCount} {user.activeRoleCount === 1 ? 'papel' : 'papéis'}
                    </button>
                  )}
                </Td>
                <Td>
                  <StatusBadge status={user.disabled ? 'inativo' : 'ativo'} />
                </Td>
                <Td align="right">
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                    <button
                      type="button"
                      onClick={() => { setFormError(null); setEditing(user); }}
                      className="pa-icon-btn"
                      style={{ width: 30, height: 30 }}
                      title="Editar"
                    >
                      <StrokeIcon d={Icons.pen} size={14} />
                    </button>
                    {/* Só para quem ainda não entrou: depois do primeiro acesso o convite já foi usado. */}
                    {!user.hasClerkAccount && !user.disabled && (
                      <button
                        type="button"
                        onClick={() => setToReissue(user)}
                        className="pa-icon-btn"
                        style={{ width: 30, height: 30 }}
                        title="Reenviar convite"
                        aria-label="Reenviar convite"
                      >
                        <StrokeIcon d={Icons.send} size={14} />
                      </button>
                    )}
                    {user.disabled ? (
                      <Btn
                        variant="secondary"
                        size="sm"
                        icon={Icons.check}
                        onClick={() => setDisabled.mutate({ id: user.id, disabled: false })}
                      >
                        Reativar
                      </Btn>
                    ) : (
                      <Btn variant="secondary" size="sm" onClick={() => setToDisable(user)}>
                        Desativar
                      </Btn>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
          <Pagination
            page={page}
            pageCount={Math.max(1, Math.ceil(total / USER_PAGE_SIZE))}
            total={total}
            onChange={setPage}
          />
        </>
      )}

      {showNew && (
        <UserFormModal
          saving={createUser.isPending}
          error={formError}
          onClose={() => setShowNew(false)}
          onSave={submitNew}
        />
      )}
      {editing && (
        <UserFormModal
          initial={editing}
          saving={updateUser.isPending}
          error={formError}
          onClose={() => setEditing(null)}
          onSave={submitEdit}
        />
      )}
      {managingRoles && (
        <UserRolesModal user={managingRoles} onClose={() => setManagingRoles(null)} />
      )}
      {linkingLegacy && (
        <LinkLegacyUserModal
          // Re-read from the page so the modal reflects a link made moments ago; the banner's copy of the
          // account is a stand-in only until the list catches up.
          user={items.find((u) => u.id === linkingLegacy.id) ?? linkingLegacy}
          onClose={() => setLinkingLegacy(null)}
        />
      )}
      {creatingLegacyAccess && (
        <CreateLegacyAccessModal
          user={creatingLegacyAccess}
          onClose={() => {
            // The banner offered this step for this person; once taken, it is stale.
            if (outcome?.user.id === creatingLegacyAccess.id) setOutcome(null);
            setCreatingLegacyAccess(null);
          }}
          onLinkInstead={() => {
            setLinkingLegacy(creatingLegacyAccess);
            setCreatingLegacyAccess(null);
          }}
        />
      )}
      {toDisable && (
        <ConfirmDialog
          title="Desativar usuário"
          message={
            <>
              Desativar <strong>{toDisable.name}</strong>? A pessoa perde o acesso ao sistema, mas o cadastro
              é preservado e pode ser reativado depois.
            </>
          }
          confirmLabel="Desativar"
          icon={Icons.eyeOff}
          onConfirm={() => setDisabled.mutate({ id: toDisable.id, disabled: true })}
          onClose={() => setToDisable(null)}
        />
      )}
      {toReissue && (
        <ConfirmDialog
          title="Reenviar convite"
          icon={Icons.send}
          message={
            <>
              Enviar um convite novo para <strong>{toReissue.name}</strong>? O link do convite anterior deixa
              de funcionar.
            </>
          }
          confirmLabel="Reenviar"
          // Waits for the answer, so a second click cannot send a second invitation; the outcome — sent,
          // queued or refused — is told on the line above the table, not swallowed with the dialog.
          onConfirm={() =>
            reissueInvite.mutateAsync(toReissue.id).then(
              (result) => {
                setReissueOutcome({ name: toReissue.name, sent: result.inviteSent });
                // The create banner is about this same person's invitation; it is stale now.
                if (outcome?.user.id === toReissue.id) setOutcome(null);
              },
              (error: unknown) => setReissueOutcome({ name: toReissue.name, error: messageOf(error) }),
            )
          }
          onClose={() => setToReissue(null)}
        />
      )}
    </div>
  );
}

/** An ApiError already carries operator-facing PT-BR detail; anything else gets a generic line. */
function messageOf(error: unknown): string {
  const detail = (error as { detail?: unknown })?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : 'Não foi possível salvar. Tente novamente.';
}
